import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService, CardAlertSummary, PaymentPlanModalRequestService, PaymentRenewalAlertAction, PaymentRenewalAlertService, PaymentRenewalAlertState, PlansService } from '@common/services';
import { CommerceService } from '@common/services/commerce.service';
import { UserService } from '@modules/auth/services';
import { BehaviorSubject, catchError, combineLatest, map, merge, Observable, of, Subject, switchMap, takeUntil, timer } from 'rxjs';

// Cada cuánto recomputamos el estado del banner para reflejar cambios derivados del paso del
// tiempo (ej: cruce de medianoche con la pestaña abierta). 60_000 ms es lo suficientemente fino
// para no demorar transiciones warning -> danger y el costo computacional es despreciable.
const STATE_RECOMPUTE_INTERVAL_MS = 60_000;

@Component({
    selector: 'fina-payment-renewal-banner',
    templateUrl: './payment-renewal-banner.component.html',
    styleUrls: ['./payment-renewal-banner.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PaymentRenewalBannerComponent implements OnInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly refreshState$: BehaviorSubject<void> = new BehaviorSubject<void>(void 0);
    /** Resumen del cobro automático; alimenta el banner de "acción con tarjeta". */
    private readonly cardSummary$: BehaviorSubject<CardAlertSummary | null> = new BehaviorSubject<CardAlertSummary | null>(null);
    /** Dispara la (re)carga del estado de la tarjeta. Se encadena con switchMap para
     *  cancelar el request anterior en vuelo (evita carreras al refrescarse el bundle). */
    private readonly loadCard$: Subject<void> = new Subject<void>();

    readonly state$: Observable<PaymentRenewalAlertState> = combineLatest([
        this.plansService.plansBundle$,
        this.userService.user$,
        this.cardSummary$,
        merge(this.refreshState$, timer(0, STATE_RECOMPUTE_INTERVAL_MS)),
    ]).pipe(
        map(([plansBundle, user, card]) => {
            // La acción con tarjeta (tarjeta rechazada / verificación 3DS) tiene
            // precedencia sobre el mensaje genérico de "pago pendiente".
            const cardState = this.paymentRenewalAlertService.buildCardActionState(card);
            if (cardState) {
                return cardState;
            }
            const runtimeKey = this.paymentRenewalAlertService.buildRuntimeHideKey(user?.tenantId, user?._id);
            return this.paymentRenewalAlertService.buildAlertState(plansBundle, runtimeKey);
        }),
    );

    constructor(
        private readonly plansService: PlansService,
        private readonly userService: UserService,
        private readonly paymentRenewalAlertService: PaymentRenewalAlertService,
        private readonly analyticsService: AnalyticsService,
        private readonly paymentPlanModalRequestService: PaymentPlanModalRequestService,
        private readonly commerceService: CommerceService,
        private readonly router: Router,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    ngOnInit(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
            this.document.body.classList.toggle('payment-renewal-banner-visible', state.visible);
        });

        // switchMap: cada disparo cancela el request anterior en vuelo, así una
        // respuesta vieja nunca sobreescribe a una más reciente. Se suscribe antes
        // de emitir el primer disparo (loadCard$ es un Subject "frío").
        this.loadCard$
            .pipe(
                switchMap(() => this.loadCardIfEligible$()),
                takeUntil(this.destroy$),
            )
            .subscribe(summary => this.cardSummary$.next(summary));

        // Cargar el estado de la tarjeta cuando lleguen los flags de PostHog (relee
        // `commerce.plus` si el comercio cargó tarde) y cuando el bundle de planes
        // se refresque (p. ej. tras pagar o reemplazar la tarjeta). `plansBundle$` es un BehaviorSubject: emite de forma síncrona al
        // suscribirse, así que esa suscripción ya cubre el warm-up inicial (no hace
        // falta un `loadCard$.next()` explícito, que dispararía un segundo request).
        this.analyticsService.flagsUpdated$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadCard$.next());
        this.plansService.plansBundle$.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadCard$.next());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.refreshState$.complete();
        this.cardSummary$.complete();
        this.loadCard$.complete();
        this.document.body.classList.remove('payment-renewal-banner-visible');
    }

    /**
     * Consulta el estado del cobro automático solo si el tenant es elegible
     * (jurídico), para no llamar al endpoint en la mayoría de tenants. Si no es
     * elegible o falla, emite null (el banner cae al estado genérico). Devuelve un observable para encadenarlo con switchMap y así
     * cancelar automáticamente el request anterior en vuelo.
     */
    private loadCardIfEligible$(): Observable<CardAlertSummary | null> {
        if (this.commerceService.commerce?.plus !== true) {
            return of(null);
        }
        return this.plansService.getCard$().pipe(
            map((res: any) => {
                const data = res?.data;
                return data
                    ? {
                          eligible: true,
                          hasCard: Boolean(data.hasCard),
                          enabled: Boolean(data.enabled),
                          status: data.status ?? null,
                          last4: data.card?.last4 ?? null,
                      }
                    : null;
            }),
            catchError(() => of(null)),
        );
    }

    onDismiss(): void {
        const user = this.userService.user$.value;
        const runtimeKey = this.paymentRenewalAlertService.buildRuntimeHideKey(user?.tenantId, user?._id);
        this.paymentRenewalAlertService.hideForRuntime(runtimeKey);
        this.refreshState$.next();
    }

    /**
     * CTA del banner. Las tres acciones abren el mismo flujo de pago (la modal ya
     * se adapta: preselecciona la tarjeta guardada, ofrece reemplazarla si fue
     * rechazada, o "Pagar con mi tarjeta" para resolver el 3DS). Solo cambia el
     * evento de analítica según el motivo.
     */
    onActionClick(action: PaymentRenewalAlertAction = 'register'): void {
        const eventByAction: Record<PaymentRenewalAlertAction, string> = {
            'update-card': 'click_alert_update_card_home',
            'pay-card': 'click_alert_verify_card_home',
            register: 'click_alert_payment_home',
        };
        this.analyticsService.analyticsCapture(eventByAction[action]);

        if (this.isPlansRoute()) {
            this.paymentPlanModalRequestService.requestOpen();
            return;
        }

        this.router.navigate(['/comercio/plans']).then(navigated => {
            if (navigated) {
                this.paymentPlanModalRequestService.requestOpen();
            }
        });
    }

    private isPlansRoute(): boolean {
        return this.router.url.split(/[?#]/)[0].replace(/\/$/, '') === '/comercio/plans';
    }
}
